import React, { useState, useRef, useEffect } from "react";
import { usePdpProduct, usePdpFocusedProduct } from "@/features/pdp/PdpContext";
import { CoverImage } from "@/components/CoverImage";
import { CoverVideo } from "@/components/CoverVideo";
import { Carousel } from "@/components/carousel/Carousel";
import { ThumbnailGroup } from "./ThumbnailGroup";
import { PdpBadge } from "@/modules/PdpFlow";
import type { Swiper as SwiperType } from "swiper";
import { Button } from "@/components/Button";
import { VideoModal } from "./VideoModal";
import { keyboardTrigger } from "@/helpers/utils";

export const ThumbnailCarouselGallery = () => {
  const product = usePdpProduct();
  const focusedProduct = usePdpFocusedProduct();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const handleClose = () => setOpen(false);

  const onThumbnailClick = (index: number) => {
    // Navigate the swiper to the clicked thumbnail index
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
    setSelectedImageIndex(index);
  };

  const onSlideChange = (index: number) => {
    setSelectedImageIndex(index);
  };

  // Reset gallery to first image when product variant changes
  React.useEffect(() => {
    setSelectedImageIndex(0);
    if (swiperRef.current) {
      if (swiperRef.current.loopedSlides) {
        swiperRef.current.slideToLoop(0);
      } else {
        swiperRef.current.slideTo(0);
      }
    }
  }, [product.id]);

  // Filter out videos for thumbnail-carousel mode
  const mediaWithoutVideos = product.media.filter(
    (item) => item.mediaType !== "video"
  );

  // Build carousel items (excluding videos)
  const items = mediaWithoutVideos.map((item, i) => (
    <div key={item.url} className="flex h-full w-full">
      <PdpBadge index={i} />
      <CoverImage
        src={item.url}
        alt={item.alt ?? ""}
        width={item.width ?? undefined}
        height={item.height ?? undefined}
        loading={i === 0 ? "eager" : "lazy"}
      />
    </div>
  ));

  const videoItem = product.media.find((item) => item.mediaType === "video");

  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const setThumbnailRef = (index: number, node: HTMLButtonElement | null) => {
    thumbnailRefs.current[index] = node;
  };

  useEffect(() => {
    const selectedThumbnail = thumbnailRefs.current[selectedImageIndex];
    if (selectedThumbnail) {
      selectedThumbnail.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedImageIndex]);

  return (
    <>
      {videoItem && (
        <VideoModal
          open={open}
          onClose={handleClose}
          videoItem={videoItem}
          productTitle={product.title}
          autoPlay={true}
        />
      )}
      <div className="flex h-full flex-col">
        <div className="relative flex-1">
          <Carousel
            items={items}
            arrows={{ variant: "half-circle", gutter: "none" }}
            dots="none"
            slideGap={false}
            slidePosition="stretch"
            onSlideChange={onSlideChange}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
            }}
          />
          {selectedImageIndex === 0 &&
            product.media.some((item) => item.mediaType === "video") && (
              <Button
                className="absolute bottom-[23px] left-[22px] z-10 flex h-[30px] items-center gap-[6px] rounded-[3px] border-2 border-[#000D8C] bg-transparent px-3 py-2 text-sm text-[#000D8C]"
                onClick={() => setOpen(true)}
                onKeyDown={keyboardTrigger(() => setOpen(true))}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 5.19615L-4.89399e-07 10.3923L-3.51373e-08 -8.7024e-07L9 5.19615Z"
                    fill="#000D8C"
                  />
                </svg>
                PLAY VIDEO
              </Button>
            )}
        </div>
        {mediaWithoutVideos.length > 1 && (
          <div className="pdp-thumbnail-scroll mt-1 flex overflow-x-auto px-1 pb-2">
            <ThumbnailGroup
              onThumbnailClick={onThumbnailClick}
              selectedIndex={selectedImageIndex}
              className="flex-row gap-2"
              media={mediaWithoutVideos}
              setThumbnailRef={setThumbnailRef}
            />
          </div>
        )}
      </div>
    </>
  );
};
